<template>
  <v-theme-provider theme="customerWorkspace">
    <div class="customer-workspace">
      <div class="workspace-toolbar">
 <v-autocomplete
  :model-value="customerId || null"
  :items="accounts"
  item-title="label"
  item-value="id"
  label="Select customer"
  variant="outlined"
  density="compact"
  hide-details
  clearable
  no-filter
  :loading="accountLoading"
  class="customer-select"
  :menu-props="{ contentClass: 'my-custom-dropdown' }"
  @update:search="searchAccounts"
  @update:model-value="chooseCustomer"
>
  <template #append-item>
    <template v-if="accountHasMore">
      <v-divider />

      <div class="pa-2" @mousedown.prevent>
        <v-btn
          block
          variant="text"
          color="primary"
          :loading="accountLoading"
          :disabled="accountLoading"
          @click.stop="loadMoreAccounts"
        >
          More customers
        </v-btn>
      </div>
    </template>
  </template>
</v-autocomplete>

        <v-spacer />
        <template v-if="customer && !loading">
          <v-btn
            variant="outlined"
            size="small"
            @click="reload"
            >Refresh</v-btn
          >
          <v-btn
            v-if="allowed('overview', 'update')"
            variant="outlined"
            size="small"
            @click="openBasic"
            >Edit Profile</v-btn
          >
          <v-menu
            ><template #activator="{ props }"
              ><v-btn
                v-bind="props"
                variant="outlined"
                size="small"
                append-icon="mdi-chevron-down"
                >Actions</v-btn
              ></template
            ><v-list density="compact"
              ><v-list-item
                title="View data quality"
                @click="qualityOpen = true" /><v-list-item
                v-if="allowed('reports')"
                title="Print account summary"
                @click="printSummary" /><v-list-item
                v-if="allowed('audit')"
                title="View audit trail"
                @click="tab = 'audit'" /></v-list
          ></v-menu>
        </template>
      </div>
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="ma-4"
        closable
        @click:close="error = ''"
        >{{ error }}</v-alert
      >
      <div
        v-if="loading"
        class="workspace-empty"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
        <p>Loading customer profile…</p>
      </div>
      <div
        v-else-if="!customer"
        class="workspace-empty"
      >
        <v-icon
          size="52"
          color="primary"
          >mdi-account-search-outline</v-icon
        >
        <h1>Customer Profile & SAM</h1>
        <p>Select a customer to view the complete account workspace.</p>
      </div>
      <template v-else>
        <header class="profile-header">
          <div class="d-flex align-start justify-space-between flex-wrap ga-4">
            <div>
              <div class="d-flex align-center ga-2 flex-wrap">
                <h1>{{ customer.company_name }}</h1>
                <v-chip
                  v-if="customer.trade_name"
                  size="small"
                  color="grey-lighten-3"
                  variant="flat"
                  >{{ customer.trade_name }}</v-chip
                ><v-chip
                  size="small"
                  :color="customer.status ? 'success' : 'grey'"
                  variant="tonal"
                  >● {{ customer.status ? "Active" : "Inactive" }}</v-chip
                ><v-chip
                  v-if="profile.selected_tier"
                  size="small"
                  color="secondary"
                  variant="flat"
                  >{{ profile.selected_tier }}</v-chip
                >
              </div>
              <div class="profile-meta">
                Account Owner: <b>{{ customer.owner_name || "Unassigned" }}</b> &nbsp;·&nbsp;
                Division: <b>{{ (customer.divisions || []).join(", ") || "—" }}</b> &nbsp;·&nbsp;
                Customer ID: <b>{{ customer.code }}</b>
              </div>
            </div>
            <button
              class="completion-button"
              @click="qualityOpen = true"
            >
              <v-progress-circular
                :model-value="completion.percentage || 0"
                size="46"
                width="4"
                color="primary"
                ><span>{{ completion.percentage || 0 }}%</span></v-progress-circular
              ><span>Profile Setup<br /><b>View detail →</b></span>
            </button>
          </div>
          <v-tabs
            v-model="tab"
            color="primary"
            class="mt-3"
            show-arrows
            ><v-tab
              v-for="item in visibleTabs"
              :key="item.key"
              :value="item.key"
              >{{ item.label }}</v-tab
            ></v-tabs
          >
        </header>
        <div class="workspace-content">
          <v-window v-model="tab">
            <v-window-item value="overview">
              <WorkspaceStats :items="overviewStats" />
              <v-row
                dense
                class="mt-3"
              >
                <v-col
                  cols="12"
                  md="7"
                >
                  <WorkspaceCard
                    title="Basic Information"
                    subtitle="Legal, registration and contact details"
                    ><template #actions
                      ><v-chip
                        size="small"
                        :color="isStrategic ? 'success' : 'grey'"
                        variant="tonal"
                        >{{ isStrategic ? "SAM unlocked" : "Standard account" }}</v-chip
                      ></template
                    >
                    <div class="kv-grid mt-3">
                      <div
                        v-for="f in basicFacts"
                        :key="f.label"
                        :class="{ full: f.full }"
                      >
                        <div class="k">{{ f.label }}</div>
                        <div class="v">{{ f.value ?? "—" }}</div>
                      </div>
                    </div></WorkspaceCard
                  >
                  <WorkspaceCard
                    title="Business Size"
                    class="mt-4"
                    ><div class="kv-grid mt-3">
                      <div
                        v-for="f in businessFacts"
                        :key="f.label"
                      >
                        <div class="k">{{ f.label }}</div>
                        <div class="v">{{ f.value ?? "—" }}</div>
                      </div>
                    </div></WorkspaceCard
                  >
                </v-col>
                <v-col
                  cols="12"
                  md="5"
                >
                  <WorkspaceCard title="Factor Breakdown"
                    ><div
                      v-for="f in factorRows"
                      :key="f.label"
                      class="mt-3"
                    >
                      <div class="bar-row">
                        <span>{{ f.label }}</span
                        ><b>{{ f.value ?? "Not available" }}</b>
                      </div>
                      <v-progress-linear
                        :model-value="f.value || 0"
                        :color="f.color"
                        height="7"
                        rounded
                      /></div
                  ></WorkspaceCard>
                  <WorkspaceCard
                    title="Credit and Payment Summary"
                    class="mt-4"
                    ><template v-if="allowed('credit')"
                      ><div class="kv-grid mt-3">
                        <div
                          v-for="f in creditFacts"
                          :key="f.label"
                        >
                          <div class="k">{{ f.label }}</div>
                          <div class="v">{{ f.value ?? "—" }}</div>
                        </div>
                      </div>
                      <p class="source-note">{{ financial.source }}</p></template
                    >
                    <p
                      v-else
                      class="empty-note"
                    >
                      Credit information requires credit-view permission.
                    </p></WorkspaceCard
                  >
                </v-col>
              </v-row>
              <WorkspaceCard
                title="Relationship Timeline"
                subtitle="Recent milestones and interactions"
                class="mt-4"
                ><template #actions
                  ><v-btn
                    v-if="allowed('activity')"
                    variant="text"
                    color="primary"
                    size="small"
                    @click="tab = 'activity'"
                    >View All</v-btn
                  ></template
                ><v-timeline
                  v-if="rows('activity').length"
                  density="compact"
                  side="end"
                  line-inset="6"
                  ><v-timeline-item
                    v-for="r in rows('activity').slice(0, 6)"
                    :key="r.id"
                    dot-color="primary"
                    size="8"
                    ><div class="timeline-item">
                      <b>{{ date(r.activity_at) }}</b
                      ><span>{{ r.discussion }}</span>
                    </div></v-timeline-item
                  ></v-timeline
                >
                <p
                  v-else
                  class="empty-note"
                >
                  No interactions recorded yet.
                </p></WorkspaceCard
              >
            </v-window-item>
            <v-window-item value="commercial">
              <WorkspaceStats :items="commercialStats" />
              <v-row
                dense
                class="mt-3"
                ><v-col
                  cols="12"
                  md="7"
                  ><WorkspaceCard
                    title="Sales and Profitability Statement"
                    subtitle="Approved active invoices · grouped by currency"
                    ><v-table
                      ><thead>
                        <tr>
                          <th>Currency</th>
                          <th>Sales Value</th>
                          <th>COGS</th>
                          <th>Gross Profit</th>
                          <th>Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="r in financial.currency_totals || []"
                          :key="r.currency_id"
                        >
                          <td>{{ r.currency || r.currency_id || "Unspecified" }}</td>
                          <td>{{ number(r.sales) }}</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                        </tr>
                      </tbody></v-table
                    >
                    <p class="source-note">
                      COGS and gross profit are not available from the approved invoice totals.
                    </p></WorkspaceCard
                  ><WorkspaceCard
                    title="Performance Analytics"
                    subtitle="Sales trend, last 12 months"
                    class="mt-4"
                    ><WorkspaceSalesChart :rows="financial.monthly || []" /></WorkspaceCard></v-col
                ><v-col
                  cols="12"
                  md="5"
                  ><WorkspaceCard title="Wallet Share by Product"
                    ><div
                      v-for="r in rows('product_gap')"
                      :key="r.id"
                      class="mt-3"
                    >
                      <div class="bar-row">
                        <span>{{ r.product_name }}</span
                        ><b>{{ percent(r.wallet_share) }}</b>
                      </div>
                      <v-progress-linear
                        :model-value="r.wallet_share || 0"
                        color="primary"
                        height="7"
                        rounded
                      />
                    </div>
                    <p
                      v-if="!rows('product_gap').length"
                      class="empty-note"
                    >
                      Add product requirements to calculate wallet share.
                    </p></WorkspaceCard
                  ><WorkspaceCard
                    title="Commercial Intelligence"
                    class="mt-4"
                    ><p class="empty-note">
                      {{ profile.competitor_pricing_notes || "No competitor notes recorded." }}
                    </p>
                    <div class="kv-grid">
                      <div>
                        <div class="k">Estimate Period</div>
                        <div class="v">{{ customer.estimate_period || "—" }}</div>
                      </div>
                      <div>
                        <div class="k">Estimate Source</div>
                        <div class="v">{{ customer.estimate_source || "—" }}</div>
                      </div>
                    </div></WorkspaceCard
                  ></v-col
                ></v-row
              >
            </v-window-item>
            <v-window-item value="credit">
              <WorkspaceStats
                :items="[
                  {
                    label: 'Credit Utilization',
                    value: '—',
                    foot: 'Currency-matched limit required',
                  },
                  {
                    label: 'Outstanding Balance',
                    value: financialValue('outstanding'),
                    foot: 'Approved active invoices',
                  },
                  {
                    label: 'Risk Assessment',
                    value: 'Not available',
                    foot: 'Risk model not configured',
                  },
                ]"
              />
              <v-row
                dense
                class="mt-3"
                ><v-col
                  cols="12"
                  md="7"
                  ><WorkspaceCard
                    title="Receivables Aging Schedule"
                    subtitle="Outstanding by invoice age"
                    ><v-table
                      ><thead>
                        <tr>
                          <th>Aging Bucket</th>
                          <th>Currency</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="r in financial.aging || []"
                          :key="r.currency + r.bucket"
                        >
                          <td>{{ r.bucket }}</td>
                          <td>{{ r.currency }}</td>
                          <td>{{ number(r.amount) }}</td>
                        </tr>
                      </tbody></v-table
                    >
                    <p
                      v-if="!financial.aging?.length"
                      class="empty-note"
                    >
                      No outstanding approved invoices.
                    </p>
                    <p class="source-note">{{ financial.aging_basis }}</p></WorkspaceCard
                  ></v-col
                ><v-col
                  cols="12"
                  md="5"
                  ><WorkspaceCard title="Risk Factor Snapshot"
                    ><div class="kv-grid mt-3">
                      <div
                        v-for="f in creditFacts.slice(0, 4)"
                        :key="f.label"
                      >
                        <div class="k">{{ f.label }}</div>
                        <div class="v">{{ f.value }}</div>
                      </div>
                    </div>
                    <p class="source-note">
                      {{ profile.payment_behavior_notes || "No payment behaviour note recorded." }}
                    </p></WorkspaceCard
                  ></v-col
                ></v-row
              >
              <WorkspaceCard
                title="Payment & Invoice History Ledger"
                subtitle="Most recent approved invoices first"
                class="mt-4"
                ><v-table
                  ><thead>
                    <tr>
                      <th>Invoice No</th>
                      <th>Invoice Date</th>
                      <th>Currency</th>
                      <th>Amount</th>
                      <th>Paid</th>
                      <th>Outstanding</th>
                      <th>Next Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in financial.invoices || []"
                      :key="r.id"
                    >
                      <td>
                        <b>{{ r.code }}</b>
                      </td>
                      <td>{{ date(r.sale_invoice_date) }}</td>
                      <td>{{ r.currency || "—" }}</td>
                      <td>{{ number(r.total) }}</td>
                      <td>{{ number(r.paid) }}</td>
                      <td>{{ number(r.due) }}</td>
                      <td>{{ date(r.next_payment_date) }}</td>
                    </tr>
                  </tbody></v-table
                >
                <p
                  v-if="!financial.invoices?.length"
                  class="empty-note"
                >
                  No approved invoice history available.
                </p></WorkspaceCard
              >
            </v-window-item>
            <v-window-item value="stakeholders">
              <WorkspaceStats :items="stakeholderStats" />
              <v-alert
                v-if="!completion.required_functions_configured"
                type="info"
                variant="tonal"
                class="mt-4"
                >Set required stakeholder functions in Edit Profile to measure department
                coverage.</v-alert
              >
              <WorkspaceRecords
                v-bind="recordProps('stakeholder')"
                title="Stakeholder Map"
                button="Add Stakeholder"
                class="mt-4"
                @edit="edit('stakeholder', $event)"
              />
              <v-row
                dense
                class="mt-3"
                ><v-col
                  cols="12"
                  md="7"
                  ><WorkspaceCard
                    title="Influence vs. Relationship Matrix"
                    subtitle="Saved contacts with influence and relationship scores"
                    ><svg
                      class="matrix mt-3"
                      viewBox="0 0 460 260"
                    >
                      <rect
                        x="0"
                        y="0"
                        width="230"
                        height="130"
                        fill="#FCE7E8"
                        opacity=".5"
                      />
                      <rect
                        x="230"
                        y="0"
                        width="230"
                        height="130"
                        fill="#E3F6EC"
                        opacity=".6"
                      />
                      <line
                        x1="230"
                        y1="0"
                        x2="230"
                        y2="260"
                        stroke="#DDE2ED"
                      />
                      <line
                        x1="0"
                        y1="130"
                        x2="460"
                        y2="130"
                        stroke="#DDE2ED"
                      />
                      <text
                        x="8"
                        y="18"
                        font-size="11"
                        fill="#B96E05"
                      >
                        Low influence · Strong relationship
                      </text>
                      <text
                        x="245"
                        y="18"
                        font-size="11"
                        fill="#178A5B"
                      >
                        High influence · Strong relationship
                      </text>
                      <g
                        v-for="s in scoredStakeholders"
                        :key="s.id"
                      >
                        <circle
                          :cx="35 + (s.influence_score - 1) * 95"
                          :cy="230 - (s.relationship_score - 1) * 45"
                          r="7"
                          :fill="s.blocker ? '#D33A44' : s.champion ? '#178A5B' : '#2E56E8'"
                        />
                        <text
                          :x="46 + (s.influence_score - 1) * 95"
                          :y="234 - (s.relationship_score - 1) * 45"
                          font-size="10"
                          fill="#4B5567"
                        >
                          {{ initials(s.name) }}
                        </text>
                        <title>{{ s.name }}</title>
                      </g>
                    </svg>
                    <p class="source-note">
                      X: influence (1–5) · Y: relationship strength (1–5)
                    </p></WorkspaceCard
                  ></v-col
                ><v-col
                  cols="12"
                  md="5"
                  ><WorkspaceCard title="Required Functions Mapped"
                    ><div
                      v-for="f in profile.required_functions || []"
                      :key="f"
                      class="mt-3"
                    >
                      <div class="bar-row">
                        <span>{{ f }}</span
                        ><b>{{
                          (data.statistics?.mapped_functions || []).includes(f)
                            ? "Mapped"
                            : "Missing"
                        }}</b>
                      </div>
                      <v-progress-linear
                        :model-value="
                          (data.statistics?.mapped_functions || []).includes(f) ? 100 : 0
                        "
                        color="success"
                        height="7"
                        rounded
                      /></div></WorkspaceCard></v-col
              ></v-row>
            </v-window-item>
            <v-window-item value="gap-analysis">
              <WorkspaceStats :items="gapStats" />
              <v-row
                dense
                class="mt-3"
                ><v-col
                  cols="12"
                  md="8"
                  ><WorkspaceRecords
                    v-bind="recordProps('product_gap')"
                    title="Product Requirement & Gap Matrix"
                    @edit="edit('product_gap', $event)" /></v-col
                ><v-col
                  cols="12"
                  md="4"
                  ><WorkspaceCard title="Wallet Share by Product"
                    ><div
                      v-for="r in rows('product_gap')"
                      :key="r.id"
                      class="mt-3"
                    >
                      <div class="bar-row">
                        <span>{{ r.product_name }}</span
                        ><b>{{ percent(r.wallet_share) }}</b>
                      </div>
                      <v-progress-linear
                        :model-value="r.wallet_share || 0"
                        color="primary"
                        height="7"
                        rounded
                      />
                    </div>
                    <p
                      v-if="!rows('product_gap').length"
                      class="empty-note"
                    >
                      No product requirements yet.
                    </p></WorkspaceCard
                  ><WorkspaceRecords
                    v-bind="recordProps('compliance')"
                    title="Compliance & Audit Checklist"
                    class="mt-4"
                    @edit="edit('compliance', $event)" /></v-col
              ></v-row>
            </v-window-item>
            <v-window-item value="swot">
              <v-alert
                variant="tonal"
                color="primary"
                icon="mdi-information-outline"
                ><b>Top strength:</b> {{ topSwot("Strength") }} &nbsp;·&nbsp; <b>Top threat:</b>
                {{ topSwot("Threat") }}</v-alert
              >
              <v-row
                dense
                class="mt-3"
                ><v-col
                  v-for="q in quadrants"
                  :key="q.type"
                  cols="12"
                  md="6"
                  ><WorkspaceCard
                    :title="q.title"
                    :subtitle="q.sub"
                    :style="{ borderTop: '3px solid ' + q.color }"
                    ><div
                      v-for="r in rows('swot').filter((s) => s.type === q.type)"
                      :key="r.id"
                      class="swot-item"
                    >
                      <div>
                        {{ r.statement }}
                        <p
                          v-if="r.competitor_name"
                          class="card-eyebrow"
                        >
                          Competitor: {{ r.competitor_name }}
                        </p>
                        <small>Review {{ date(r.review_date) }}</small>
                      </div>
                      <v-chip
                        size="x-small"
                        :color="r.impact_level === 'High' ? 'warning' : 'grey'"
                        >{{ r.impact_level }} Impact</v-chip
                      ><v-btn
                        v-if="allowed('swot', 'update')"
                        icon="mdi-pencil-outline"
                        size="x-small"
                        variant="text"
                        @click="edit('swot', r)"
                      />
                    </div>
                    <p
                      v-if="!rows('swot').some((s) => s.type === q.type)"
                      class="empty-note"
                    >
                      No {{ q.title.toLowerCase() }} recorded.
                    </p>
                    <v-btn
                      v-if="allowed('swot', 'insert')"
                      block
                      variant="tonal"
                      size="small"
                      prepend-icon="mdi-plus"
                      @click="edit('swot', null, { type: q.type })"
                      >Add {{ q.type }}</v-btn
                    ></WorkspaceCard
                  ></v-col
                ></v-row
              >
              <WorkspaceRecords
                v-bind="recordProps('swot')"
                title="SWOT Review Register"
                class="mt-4"
                @edit="edit('swot', $event)"
              />
            </v-window-item>
            <v-window-item value="activity">
              <WorkspaceStats :items="activityStats" />
              <v-row
                dense
                class="mt-3"
                ><v-col
                  cols="12"
                  md="8"
                  ><WorkspaceRecords
                    v-bind="recordProps('activity')"
                    title="Activity History Log"
                    button="Add Activity"
                    @edit="edit('activity', $event)" /></v-col
                ><v-col
                  cols="12"
                  md="4"
                  ><WorkspaceCard title="Activity Type Share"
                    ><div
                      v-for="kind in ['Visit', 'Call', 'Sample', 'Offer', 'Complaint']"
                      :key="kind"
                      class="mt-3"
                    >
                      <div class="bar-row">
                        <span>{{ kind }}</span
                        ><b>{{
                          rows("activity").filter((r) => r.activity_type === kind).length
                        }}</b>
                      </div>
                      <v-progress-linear
                        :model-value="
                          rows('activity').length
                            ? (rows('activity').filter((r) => r.activity_type === kind).length /
                                rows('activity').length) *
                              100
                            : 0
                        "
                        color="primary"
                        height="7"
                        rounded
                      />
                    </div>
                    <p class="source-note">Recent 25 activities</p></WorkspaceCard
                  ><WorkspaceCard
                    title="Upcoming Follow-ups"
                    class="mt-4"
                    ><div
                      v-for="r in rows('activity')
                        .filter((r) => r.activity_status === 'Open')
                        .slice(0, 5)"
                      :key="r.id"
                      class="swot-item"
                    >
                      <div>
                        {{ r.next_action }}
                        <p class="card-eyebrow">{{ date(r.next_action_at) }}</p>
                      </div>
                      <v-chip
                        size="x-small"
                        :color="r.display_status === 'Overdue' ? 'warning' : 'grey'"
                        >{{ r.display_status }}</v-chip
                      >
                    </div></WorkspaceCard
                  ></v-col
                ></v-row
              >
            </v-window-item>
            <v-window-item value="sam-performance">
              <v-alert
                v-if="!isStrategic"
                type="info"
                variant="tonal"
                >SAM Performance is available for customers with the Strategic tier.</v-alert
              >
              <template v-else
                ><v-tabs
                  v-model="samTab"
                  color="primary"
                  class="sam-subtabs"
                  ><v-tab value="performance">SAM Overview</v-tab
                  ><v-tab
                    v-if="allowed('pipeline')"
                    value="pipeline"
                    >Pipeline Detail</v-tab
                  ><v-tab value="risk">Risk Analysis</v-tab></v-tabs
                >
                <template v-if="samTab === 'performance'"
                  ><WorkspaceStats :items="samStats" /><v-row
                    dense
                    class="mt-3"
                    ><v-col
                      cols="12"
                      md="7"
                      ><WorkspaceRecords
                        v-bind="recordProps('action_plan')"
                        title="Account Action Plan"
                        @edit="edit('action_plan', $event)" /></v-col
                    ><v-col
                      cols="12"
                      md="5"
                      ><WorkspaceCard title="Customer Feedback Surveys"
                        ><template #actions
                          ><v-btn
                            v-if="allowed('feedback', 'insert')"
                            size="small"
                            color="primary"
                            @click="edit('feedback')"
                            >Add Survey</v-btn
                          ></template
                        >
                        <div
                          v-for="f in feedbackMetrics"
                          :key="f.key"
                          class="mt-3"
                        >
                          <div class="bar-row">
                            <span>{{ f.label }}</span
                            ><b>{{ latestFeedback[f.key] ?? "—" }} / 5</b>
                          </div>
                          <v-progress-linear
                            :model-value="(latestFeedback[f.key] || 0) * 20"
                            color="primary"
                            height="7"
                            rounded
                          />
                        </div>
                        <v-sheet
                          color="#EAF0FE"
                          rounded="lg"
                          class="pa-3 mt-3"
                          ><div class="stat-label">Weighted Customer Satisfaction Index</div>
                          <b>{{ percent(latestFeedback.score) }}</b></v-sheet
                        >
                        <p class="source-note">
                          Quality 30% · Price 25% · Shipment 25% · Responsiveness 20%
                        </p></WorkspaceCard
                      ></v-col
                    ></v-row
                  ><WorkspaceCard
                    title="SAM Score Breakdown"
                    class="mt-4"
                    ><div class="score-strip">
                      <span
                        v-for="(f, i) in samWeights"
                        :key="f.label"
                        :style="{
                          width: f.weight + '%',
                          background: ['#2E56E8', '#5B7BEE', '#8FA6F4', '#B7C6F8', '#DCE4FC'][i],
                        }"
                      />
                    </div>
                    <div class="legend-row">
                      <span
                        v-for="f in samWeights"
                        :key="f.label"
                        >{{ f.label }} ({{ f.weight }}%)</span
                      >
                    </div>
                    <p class="source-note">
                      Composite score awaits target and pipeline progress definitions.
                    </p></WorkspaceCard
                  ></template
                >
                <WorkspaceRecords
                  v-else-if="samTab === 'pipeline'"
                  v-bind="recordProps('opportunity')"
                  title="Product Pipeline Overview"
                  @edit="edit('opportunity', $event)"
                />
                <WorkspaceCard
                  v-else
                  title="Customer Overall Risk Profile"
                  subtitle="Risk factors and mitigation"
                  ><div class="chart-empty">
                    <v-icon
                      size="42"
                      color="warning"
                      >mdi-shield-alert-outline</v-icon
                    >
                    <p>Risk score is not available.</p>
                  </div>
                  <div class="kv-grid">
                    <div>
                      <div class="k">Payment behaviour</div>
                      <div class="v">
                        {{
                          allowed("credit") ? profile.payment_behavior_notes || "—" : "Restricted"
                        }}
                      </div>
                    </div>
                    <div>
                      <div class="k">Competitor intelligence</div>
                      <div class="v">{{ profile.competitor_pricing_notes || "—" }}</div>
                    </div>
                  </div></WorkspaceCard
                >
              </template>
            </v-window-item>
            <v-window-item value="pipeline"
              ><WorkspaceRecords
                v-bind="recordProps('opportunity')"
                title="Product Pipeline Overview"
                @edit="edit('opportunity', $event)"
            /></v-window-item>
            <v-window-item value="feedback"
              ><WorkspaceRecords
                v-bind="recordProps('feedback')"
                title="Customer Feedback Surveys"
                @edit="edit('feedback', $event)"
            /></v-window-item>
            <v-window-item value="reports"
              ><WorkspaceCard
                title="Customer Reports"
                :subtitle="'Export or view reportsManagement for ' + customer.company_name"
                ><v-row
                  dense
                  class="mt-3"
                  ><v-col
                    v-for="r in reportCards"
                    :key="r.title"
                    cols="12"
                    md="4"
                    ><v-card
                      variant="outlined"
                      class="pa-4 h-100"
                      ><h3>{{ r.title }}</h3>
                      <p class="card-eyebrow mb-3">{{ r.description }}</p>
                      <v-btn
                        size="small"
                        variant="tonal"
                        @click="printSummary(r.title)"
                        >Print / Save PDF</v-btn
                      ></v-card
                    ></v-col
                  ></v-row
                ></WorkspaceCard
              ><WorkspaceCard
                title="Customer Accounts"
                subtitle="Search and browse permitted customer accounts"
                class="mt-4"
              >
                <v-text-field
                  v-model="portfolioSearch"
                  label="Search customers"
                  density="compact"
                  variant="outlined"
                  @update:model-value="portfolioPage"
                /><v-table
                  ><thead>
                    <tr>
                      <th>Code</th>
                      <th>Customer</th>
                      <th>Tier</th>
                      <th>Verification</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="a in portfolio"
                      :key="a.id"
                      class="clickable"
                      @click="chooseCustomer(a.id)"
                    >
                      <td>{{ a.code }}</td>
                      <td>{{ a.company_name }}</td>
                      <td>{{ a.customer_profile?.selected_tier || "—" }}</td>
                      <td>{{ a.customer_profile?.verification_state || "Draft" }}</td>
                    </tr>
                  </tbody></v-table
                >
                <v-pagination
                  v-model="portfolioPage"
                  :length="portfolioPages"
                  :total-visible="7"
                  rounded="circle"
                  color="primary"
                  @update:model-value="loadPortfolio"
                /> </WorkspaceCard
            ></v-window-item>
            <v-window-item value="audit"
              ><WorkspaceCard
                title="Audit Trail"
                subtitle="Material changes to this customer"
                ><v-table
                  ><thead>
                    <tr>
                      <th>Event</th>
                      <th>Actor</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in rows('audit')"
                      :key="r.id"
                    >
                      <td>{{ r.event }}</td>
                      <td>{{ r.actor_id }}</td>
                      <td>{{ date(r.created_at) }}</td>
                    </tr>
                  </tbody></v-table
                ></WorkspaceCard
              ></v-window-item
            >
          </v-window>
        </div>
      </template>

      <v-dialog
        v-model="basicOpen"
        max-width="760"
        scrollable
        persistent
        ><v-card class="workspace-dialog"
          ><v-card-title class="dialog-title"
            ><span>Edit Customer Profile</span
            ><div class="dialog-title-actions">
              <v-btn
                prepend-icon="mdi-book-open-page-variant"
                size="small"
                variant="tonal"
                color="primary"
                @click="manualOpen = true"
                >Entry Guide</v-btn
              ><v-btn
                icon="mdi-close"
                size="small"
                variant="text"
                aria-label="Close profile form"
                @click="closeBasic" />
            </div></v-card-title
          ><v-card-text
            ><v-alert
              v-if="formError"
              type="error"
              variant="tonal"
              class="mb-4"
              >{{ formError }}</v-alert
            ><template
              v-for="section in basicSections"
              :key="section.title"
              ><div class="section-label">{{ section.title }}</div>
              <v-row dense
                ><v-col
                  v-for="f in section.fields"
                  :key="f.key"
                  cols="12"
                  :sm="f.type === 'textarea' ? 12 : 6"
                  class="pb-3"
                  ><WorkspaceField
                    :field="f"
                    v-model="basicForm[f.key]"
                    :lookups="lookups"
                    :error="fieldErrors[f.key]"
                    :disabled="restricted(f.key)" /></v-col></v-row></template></v-card-text
          ><v-card-actions class="pa-4"
            ><v-spacer /><v-btn
              variant="outlined"
              @click="closeBasic"
              >Discard Changes</v-btn
            ><v-btn
              color="primary"
              variant="flat"
              :loading="saving"
              @click="saveBasic"
              >Save Changes</v-btn
            ></v-card-actions
          ></v-card
        ></v-dialog
      >

      <v-dialog
        v-model="manualOpen"
        max-width="820"
        scrollable
        ><v-card class="workspace-dialog entry-guide-dialog"
          ><v-card-title class="dialog-title"
            ><div>
              <div>Customer Profile Entry Guide</div>
              <div class="entry-guide-subtitle">প্রতিটি ঘরে কী তথ্য দেবেন, উদাহরণসহ দেখুন</div>
            </div>
            <v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              aria-label="Close entry guide"
              @click="manualOpen = false" /></v-card-title
          ><v-card-text class="entry-guide-content">
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              <b>* চিহ্নিত field বাধ্যতামূলক।</b> তথ্য লিখে Save Changes চাপুন। Dropdown থেকে option বেছে নিন; tags field-এ প্রতিটি item লিখে Enter চাপুন।
            </v-alert>
            <section
              v-for="section in basicSections"
              :key="section.title"
              class="entry-guide-section"
            >
              <h3>{{ section.title }}</h3>
              <div class="entry-guide-grid">
                <article v-for="field in section.fields" :key="field.key" class="entry-guide-item">
                  <div class="entry-guide-field-label">{{ field.label }}</div>
                  <p>{{ basicFieldGuide[field.key]?.description || "প্রতিষ্ঠানের সঠিক তথ্য দিন। জানা না থাকলে অনুমান না করে ফাঁকা রাখুন।" }}</p>
                  <div v-if="basicFieldGuide[field.key]?.example" class="entry-guide-example">
                    উদাহরণ: {{ basicFieldGuide[field.key].example }}
                  </div>
                </article>
              </div>
            </section>
            <v-alert type="warning" variant="tonal" density="compact" class="mt-4">
              BIN, TIN, Credit Terms, Credit Limit ও Payment Behaviour Notes-এর মতো সীমাবদ্ধ field আপনার permission না থাকলে edit করা যাবে না। সঠিক নথি দেখে তথ্য দিন।
            </v-alert>
          </v-card-text
          ><v-card-actions class="pa-4">
            <v-spacer />
            <v-btn color="primary" variant="flat" @click="manualOpen = false">ফর্মে ফিরে যান</v-btn>
          </v-card-actions></v-card
        ></v-dialog
      >

      <v-dialog
        :model-value="recordOpen && recordType !== 'activity'"
        max-width="560"
        scrollable
        persistent
        ><v-card class="workspace-dialog"
          ><v-card-title class="dialog-title"
            ><span>{{ recordId ? "Edit" : "Add" }} {{ recordConfig.title }}</span
            ><v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              @click="closeRecord" /></v-card-title
          ><v-card-text><RecordFields /></v-card-text
          ><v-card-actions class="pa-4"
            ><v-spacer /><v-btn
              variant="outlined"
              @click="closeRecord"
              >Cancel</v-btn
            ><v-btn
              color="primary"
              variant="flat"
              :loading="saving"
              @click="saveRecord"
              >Save {{ recordConfig.title }}</v-btn
            ></v-card-actions
          ></v-card
        ></v-dialog
      >
      <v-dialog
        :model-value="recordOpen && recordType === 'activity'"
        transition="slide-x-reverse-transition"
        max-width="460"
        content-class="activity-drawer"
        persistent
        ><v-card class="workspace-dialog"
          ><v-card-title class="dialog-title"
            ><span>Log Sales Activity</span
            ><v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeRecord" /></v-card-title
          ><v-card-text><RecordFields /></v-card-text
          ><v-card-actions class="pa-4"
            ><v-spacer /><v-btn
              variant="outlined"
              @click="closeRecord"
              >Discard</v-btn
            ><v-btn
              color="primary"
              variant="flat"
              :loading="saving"
              @click="saveRecord"
              >Save Activity</v-btn
            ></v-card-actions
          ></v-card
        ></v-dialog
      >
      <v-dialog
        v-model="qualityOpen"
        max-width="820"
        scrollable
        ><v-card class="workspace-dialog"
          ><v-card-title class="dialog-title"
            >Profile Completion & Data Quality<v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="qualityOpen = false" /></v-card-title
          ><v-card-text
            ><WorkspaceStats
              :items="[
                { label: 'Completion Score', value: percent(completion.percentage) },
                { label: 'KPI Marks', value: (completion.kpi_mark || 0) + ' / 10' },
                { label: 'Manager Verification', value: profile.verification_state || 'Draft' },
              ]"
            /><WorkspaceCard
              title="Tab Completion Breakdown"
              class="mt-4"
              ><div
                v-for="(value, key) in completion.sections"
                :key="key"
                class="mt-3"
              >
                <div class="bar-row">
                  <span>{{ key.replace("_", " ") }}</span
                  ><b>{{ value }}% (Weight {{ completion.weights?.[key] }}%)</b>
                </div>
                <v-progress-linear
                  :model-value="value"
                  :color="value >= 80 ? 'success' : 'warning'"
                  height="7"
                  rounded
                /></div></WorkspaceCard
            ><WorkspaceCard
              title="Data Quality Warnings"
              class="mt-4"
              ><v-alert
                v-if="completion.missing_fields?.length"
                type="warning"
                variant="tonal"
                >Missing:
                {{
                  completion.missing_fields.map((f) => f.replaceAll("_", " ")).join(", ")
                }}</v-alert
              >
              <p
                v-else
                class="empty-note"
              >
                Basic required fields are complete.
              </p>
              <p
                v-if="completion.stale"
                class="source-note"
              >
                Material information is older than 90 days. A 10-point staleness deduction applies.
              </p></WorkspaceCard
            ></v-card-text
          ></v-card
        ></v-dialog
      >
      <v-dialog
        v-model="conflictOpen"
        max-width="470"
        persistent
        ><v-card title="Customer changed"
          ><v-card-text
            >Another save changed this customer. Your form is still open. Refresh the latest
            customer before reapplying your changes.</v-card-text
          ><v-card-actions
            ><v-btn @click="conflictOpen = false">Keep form</v-btn
            ><v-btn
              color="primary"
              @click="refreshConflict"
              >Refresh latest</v-btn
            ></v-card-actions
          ></v-card
        ></v-dialog
      >
      <v-snackbar
        v-model="noticeOpen"
        color="success"
        >{{ notice }}</v-snackbar
      >
      <WorkspaceReport
        v-if="data && reportOpen"
        :open="reportOpen"
        :kind="reportKind"
        :data="data"
        @close="reportOpen = false"
      />
    </div>
  </v-theme-provider>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, h, defineComponent } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useTheme } from "vuetify";
import Axios from "axios";
import {
  customerProfileAccountsUrl,
  customerProfileSamUrl,
  customerProfileSamRecordUrl,
} from "@/store/module/customerProfile/api";
import WorkspaceCard from "@/components/module/customerProfile/WorkspaceCard.vue";
import WorkspaceStats from "@/components/module/customerProfile/WorkspaceStats.vue";
import WorkspaceRecords from "@/components/module/customerProfile/WorkspaceRecords.vue";
import WorkspaceField from "@/components/module/customerProfile/WorkspaceField.vue";
import WorkspaceSalesChart from "@/components/module/customerProfile/WorkspaceSalesChart.vue";
import WorkspaceReport from "@/components/module/customerProfile/WorkspaceReport.vue";
import { basicSections, forms } from "@/components/module/customerProfile/workspaceFields";
const theme = useTheme();
theme.themes.value.customerWorkspace = {
  ...theme.themes.value.light,
  colors: {
    ...theme.themes.value.light.colors,
    primary: "#2E56E8",
    secondary: "#52629E",
    success: "#178A5B",
    warning: "#B96E05",
    error: "#D33A44",
  },
};
const route = useRoute(),
  router = useRouter(),
  data = ref(null),
  loading = ref(false),
  error = ref(""),
  accounts = ref([]),
  accountLoading = ref(false),
  lookups = ref({}),
  tab = ref("overview"),
  samTab = ref("performance");
const customerId = computed(() => Number(route.query.customer_id || 0)),
  customer = computed(() => data.value?.customer),
  profile = computed(() => data.value?.profile || {}),
  financial = computed(() => data.value?.financial || {}),
  completion = computed(() => data.value?.completion || {}),
  isStrategic = computed(() => profile.value.selected_tier === "Strategic");
const selectedEmployeeId = computed(() => Number(route.query.employee_id || 0));
const currentBranchId = computed(() =>
  Number(JSON.parse(localStorage.getItem("branch") || "{}")?.id || 0),
);
const allTabs = [
  ["overview", "Overview"],
  ["commercial", "Commercial"],
  ["credit", "Credit & Payment"],
  ["stakeholders", "Stakeholders"],
  ["gap-analysis", "Gap Analysis"],
  ["swot", "SWOT"],
  ["activity", "Sales Activity"],
  ["sam-performance", "SAM Performance"],
  ["reports", "Reports"],
  ["pipeline", "Pipeline"],
  ["feedback", "Feedback"],
  ["audit", "Audit"],
].map(([key, label]) => ({ key, label }));
const visibleTabs = computed(() => allTabs.filter((t) => allowed(t.key)));
function allowed(key, ability = "view") {
  return !!data.value?.permissions?.[key]?.[ability];
}
function rows(type) {
  return data.value?.records?.[type] || [];
}
function number(n) {
  return n == null || n === ""
    ? "—"
    : new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2 }).format(Number(n));
}
function percent(n) {
  return n == null ? "—" : number(n) + "%";
}
function date(d) {
  if (!d) return "—";
  const v = new Date(d);
  return Number.isNaN(v.valueOf())
    ? "—"
    : new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(
        v,
      );
}
function money(n, currency = "") {
  return n == null ? "—" : number(n) + (currency ? " " + currency : "");
}
function financialValue(key) {
  const r = financial.value.currency_totals || [];
  return r.length === 1
    ? money(r[0][key], r[0].currency)
    : r.length > 1
      ? "Multiple currencies"
      : "—";
}
const overviewStats = computed(() => [
  { label: "Total Sales", value: financialValue("sales"), foot: "Approved invoices" },
  { label: "Gross Margin", value: "—", foot: "Not available" },
  {
    label: "Outstanding",
    value: allowed("credit") ? financialValue("outstanding") : "Restricted",
    foot: "Current balance",
  },
  { label: "Wallet Share", value: "—", foot: "See product-level share" },
  { label: "Strategic Score", value: "—", foot: "Not configured" },
  {
    label: "Account Tier",
    value: profile.value.selected_tier || "Not selected",
    foot: profile.value.verification_state || "Draft",
  },
]);
const basicFacts = computed(() => {
  const c = customer.value || {};
  return [
    { label: "Customer ID", value: c.code },
    { label: "Selected Tier", value: profile.value.selected_tier },
    { label: "Legal Name", value: c.company_name },
    { label: "Strategic Score", value: "Not available" },
    { label: "Trade Name", value: c.trade_name },
    { label: "Business Type", value: c.business_type },
    { label: "Industry", value: c.industry },
    { label: "Year Established", value: c.year_established },
    { label: "Registration No", value: c.registration_no },
    { label: "BIN", value: c.company_bin },
    { label: "TIN", value: c.company_tin },
    { label: "Website", value: c.company_website },
    { label: "Factory Address", value: c.factory_address, full: true },
    { label: "Corporate Address", value: c.company_address, full: true },
    { label: "District", value: c.district_territory },
    { label: "Country", value: c.country_name },
    { label: "Created", value: date(c.created_at) },
    { label: "Last Updated", value: date(c.updated_at) },
  ];
});
const businessFacts = computed(() => {
  const c = customer.value || {};
  return [
    { label: "Annual Turnover", value: money(c.annual_turnover, c.turnover_currency) },
    { label: "Employees", value: number(c.employee_count) },
    {
      label: "Chemical Consumption",
      value: money(c.annual_chemical_consumption, c.consumption_unit),
    },
    { label: "Estimated Wallet", value: money(c.estimated_total_wallet, c.turnover_currency) },
    { label: "Purchase Value", value: financialValue("sales") },
    { label: "Purchase Volume", value: "—" },
    { label: "CY Sales", value: "—" },
    { label: "PY Sales", value: "—" },
  ];
});
const factorRows = [
  { label: "Volume Loyalty", value: null, color: "primary" },
  { label: "Credit Integrity", value: null, color: "warning" },
];
const creditFacts = computed(() => [
  {
    label: "Credit Terms",
    value: profile.value.credit_terms == null ? "—" : profile.value.credit_terms + " Days",
  },
  { label: "Credit Limit", value: money(profile.value.credit_limit) },
  { label: "Outstanding", value: financialValue("outstanding") },
  { label: "Paid", value: financialValue("paid") },
  { label: "Overdue", value: "—" },
  { label: "Avg Payment Days", value: "—" },
  { label: "Credit Utilization", value: "—" },
  { label: "Risk Score", value: "Not available" },
]);
const commercialStats = computed(() => [
  { label: "Total Sales", value: financialValue("sales"), foot: "Approved invoices" },
  { label: "Gross Margin", value: "—", foot: "Cost reconciliation required" },
  { label: "Wallet Gap", value: "See product matrix", foot: "Calculated per currency and unit" },
]);
const stakeholderStats = computed(() => [
  {
    label: "Total Contacts",
    value: data.value?.statistics?.stakeholders ?? rows("stakeholder").length,
    foot: "Mapped customer contacts",
  },
  {
    label: "Stakeholder Coverage",
    value: percent(completion.value.sections?.stakeholder),
    foot: "Required functions mapped",
  },
  {
    label: "Active Champions",
    value: data.value?.statistics?.champions ?? 0,
    foot: "Direct support channels",
  },
  {
    label: "Active Blockers",
    value: data.value?.statistics?.blockers ?? 0,
    foot: "Relationships to review",
    color: "text-error",
  },
]);
const scoredStakeholders = computed(() =>
  rows("stakeholder").filter((s) => s.influence_score && s.relationship_score),
);
function groupedValue(rows, key, unit) {
  return !rows?.length
    ? "—"
    : rows.length === 1
      ? money(rows[0][key], rows[0][unit])
      : rows.map((r) => money(r[key], r[unit])).join(" / ");
}
const gapStats = computed(() => [
  {
    label: "Total Gap Value",
    value: groupedValue(data.value?.statistics?.gap_values, "value", "currency"),
    foot: "Current year · by currency",
  },
  {
    label: "Gap Volume",
    value: groupedValue(data.value?.statistics?.gap_volumes, "value", "uom"),
    foot: "Current year · by unit",
  },
  {
    label: "Active Pipeline",
    value: data.value?.statistics?.open_opportunities ?? 0,
    foot: "Open opportunities",
  },
  {
    label: "Weighted Pipeline",
    value: groupedValue(data.value?.statistics?.pipeline_totals, "weighted_value", "currency"),
    foot: "Open opportunities · by currency",
  },
]);
const activityStats = computed(() => [
  { label: "Monthly Activities", value: data.value?.statistics?.monthly_activities ?? 0 },
  { label: "Visits", value: data.value?.statistics?.monthly_visits ?? 0 },
  { label: "Calls", value: data.value?.statistics?.monthly_calls ?? 0 },
  { label: "Samples", value: data.value?.statistics?.monthly_samples ?? 0 },
  { label: "Completed", value: data.value?.statistics?.completed_activities ?? 0 },
  { label: "Overdue", value: data.value?.statistics?.overdue_activities ?? 0, color: "text-error" },
]);
const latestFeedback = computed(() => rows("feedback")[0] || {}),
  feedbackMetrics = [
    { key: "quality_score", label: "Quality Consistency" },
    { key: "price_score", label: "Price Competitiveness" },
    { key: "shipment_score", label: "Shipment Reliability" },
    { key: "responsiveness_score", label: "Responsiveness" },
  ];
const samStats = computed(() => [
  { label: "SAM Score", value: "—", foot: "Not configured" },
  { label: "Target Achievement", value: "—", foot: "Target not linked" },
  { label: "CRM Completeness", value: percent(completion.value.percentage) },
  { label: "Feedback Index", value: percent(latestFeedback.value.score) },
  { label: "Action Completion", value: percent(data.value?.statistics?.action_completion) },
]);
const samWeights = [
  { label: "Target", weight: 30 },
  { label: "CRM", weight: 15 },
  { label: "Feedback", weight: 15 },
  { label: "Action Plan", weight: 20 },
  { label: "Pipeline", weight: 20 },
];
const quadrants = [
  { type: "Strength", title: "Strengths", sub: "Internal · Positive", color: "#178A5B" },
  { type: "Weakness", title: "Weaknesses", sub: "Internal · Negative", color: "#B96E05" },
  { type: "Opportunity", title: "Opportunities", sub: "External · Positive", color: "#2E6BE8" },
  { type: "Threat", title: "Threats", sub: "External · Negative", color: "#D33A44" },
];
function topSwot(type) {
  return rows("swot").find((r) => r.type === type)?.statement || "Not recorded";
}
function initials(name) {
  return (name || "")
    .split(" ")
    .slice(0, 2)
    .map((s) => s[0])
    .join("");
}
function recordProps(type) {
  const key = forms[type].permission;
  return {
    customerId: customerId.value,
    type,
    version: profile.value.profile_version,
    canInsert: allowed(key, "insert"),
    canUpdate: allowed(key, "update"),
  };
}
const reportCards = [
  { title: "Account Summary", description: "Overview, commercial and credit position." },
  { title: "SAM Performance Pack", description: "Action plan and customer feedback." },
  { title: "Gap & Pipeline Report", description: "Product requirements and weighted pipeline." },
];
const portfolio = ref([]),
  portfolioPage = ref(1),
  portfolioPages = ref(1),
  portfolioSearch = ref("");
let portfolioSeq = 0;

// async function loadPortfolio(){const seq=++portfolioSeq;try{const r=await Axios.get(customerProfileAccountsUrl,{params:{page:portfolioPage.value,search:portfolioSearch.value,employee_id:selectedEmployeeId.value||undefined}});if(seq===portfolioSeq){portfolio.value=r.data.data;portfolioPages.value=r.data.meta.last_page;}}catch(e){error.value=e.response?.data?.message||'Could not load accounts.';}}
// let loadSeq=0,searchSeq=0,accountTimer,productTimer;
async function loadPortfolio() {
  const sequence = ++portfolioSeq;

  try {
    const response = await Axios.get(customerProfileAccountsUrl, {
      params: {
        page: portfolioPage.value,
        per_page: 25,
        search: portfolioSearch.value || undefined,
        employee_id: selectedEmployeeId.value || undefined,
      },
    });

    if (sequence !== portfolioSeq) {
      return;
    }

    portfolio.value = response.data?.data || [];
    portfolioPages.value = Math.max(1, Number(response.data?.meta?.last_page || 1));
  } catch (exception) {
    if (sequence === portfolioSeq) {
      error.value = exception.response?.data?.message || "Could not load customer accounts.";
    }
  }
}
let loadSeq = 0,
  searchSeq = 0,
  accountTimer,
  productTimer;
async function reload() {
  const seq = ++loadSeq;
  data.value = null;
  loading.value = false;
  error.value = "";
  if (!customerId.value) return;
  loading.value = true;
  try {
    const r = await Axios.get(customerProfileSamUrl(customerId.value));
    if (seq !== loadSeq) return;
    data.value = r.data.data;
    const c = customer.value;
    if (!accounts.value.some((a) => a.id === c.id))
      accounts.value.push({ ...c, label: c.company_name + " — " + c.code });
    lookups.value.contacts = rows("stakeholder").map((r) => ({ id: r.id, name: r.name }));
    if (!allowed(tab.value)) tab.value = visibleTabs.value[0]?.key || "overview";
  } catch (e) {
    if (seq === loadSeq) error.value = e.response?.data?.message || "Unable to load customer.";
  } finally {
    if (seq === loadSeq) loading.value = false;
  }
}
function chooseCustomer(id) {
  router.replace({
    path: "/module/customer-profile",
    query: { ...route.query, customer_id: id || undefined, tab: tab.value },
  });
}
const accountPage = ref(0);
const accountHasMore = ref(false);
const accountSearchText = ref("");

function searchAccounts(search = "") {
  clearTimeout(accountTimer);

  const seq = ++searchSeq;

  accountSearchText.value = search ?? "";
  accountPage.value = 0;
  accountHasMore.value = false;
  accountLoading.value = true;

  const selected = customer.value;

  accounts.value = selected
    ? [{
        ...selected,
        label: `${selected.company_name} — ${selected.code}`,
      }]
    : [];

  accountTimer = setTimeout(() => {
    fetchAccountPage(1, seq);
  }, 250);
}

async function fetchAccountPage(page, seq) {
  if (seq !== searchSeq) return;

  accountLoading.value = true;

  try {
    const response = await Axios.get(customerProfileAccountsUrl, {
      params: {
        search: accountSearchText.value,
        page,
        per_page: 25,
        employee_id: selectedEmployeeId.value || undefined,
      },
    });

    // Ignore an older search response.
    if (seq !== searchSeq) return;

    const rows = response.data.data.map((item) => ({
      ...item,
      label: `${item.company_name} — ${item.code}`,
    }));

    const combined =
      page === 1 ? rows : [...accounts.value, ...rows];

    const selected = customer.value;

    if (selected) {
      combined.unshift({
        ...selected,
        label: `${selected.company_name} — ${selected.code}`,
      });
    }

    // Prevent duplicate customers.
    const uniqueCustomers = new Map();

    for (const item of combined) {
      uniqueCustomers.set(String(item.id), item);
    }

    accounts.value = [...uniqueCustomers.values()];

    const meta = response.data.meta;

    accountPage.value = Number(meta.current_page);
    accountHasMore.value =
      accountPage.value < Number(meta.last_page);
  } catch (e) {
    if (seq === searchSeq) {
      error.value =
        e.response?.data?.message || "Customer search failed.";
    }
  } finally {
    if (seq === searchSeq) {
      accountLoading.value = false;
    }
  }
}

function loadMoreAccounts() {
  if (accountLoading.value || !accountHasMore.value) return;

  fetchAccountPage(accountPage.value + 1, searchSeq);
}
async function loadLookups(search = "") {
  try {
    const r = await Axios.get(customerProfileAccountsUrl + "/lookups", {
      params: {
        search,
        customer_id: customerId.value,
        product_id: recordOpen.value ? recordForm.value.product_id : undefined,
      },
    });
    lookups.value = { ...lookups.value, ...r.data.data };
  } catch (e) {
    error.value = e.response?.data?.message || "Unable to load form options.";
  }
}
function searchProducts(search) {
  clearTimeout(productTimer);
  productTimer = setTimeout(() => loadLookups(search), 300);
}
const basicOpen = ref(false),
  manualOpen = ref(false),
  basicForm = ref({}),
  recordOpen = ref(false),
  recordType = ref("stakeholder"),
  recordForm = ref({}),
  recordId = ref(null),
  saving = ref(false),
  formError = ref(""),
  fieldErrors = ref({}),
  qualityOpen = ref(false),
  conflictOpen = ref(false),
  noticeOpen = ref(false),
  notice = ref("");
let originalBasic = "",
  originalRecord = "",
  formVersion = 1;
const basicFieldGuide = {
  company_name: { description: "ট্রেড লাইসেন্স বা নিবন্ধনপত্রে থাকা প্রতিষ্ঠানের পূর্ণ আইনগত নাম লিখুন।", example: "ABC Chemicals Ltd." },
  trade_name: { description: "আইনগত নামের বদলে বাজারে ব্যবহৃত আলাদা নাম থাকলে লিখুন।", example: "ABC Paints" },
  divisions: { description: "প্রতিষ্ঠানের সংশ্লিষ্ট business division লিখুন; একাধিক হলে প্রতিটি আলাদা tag হিসেবে দিন।", example: "Paint, Construction" },
  business_type: { description: "প্রতিষ্ঠানের মূল ব্যবসার ধরন তালিকা থেকে বেছে নিন।", example: "Manufacturer" },
  industry: { description: "প্রতিষ্ঠান যে শিল্পখাতে কাজ করে তা লিখুন।", example: "Paint & Coatings" },
  year_established: { description: "প্রতিষ্ঠান শুরু হওয়ার চার অঙ্কের সাল লিখুন।", example: "2008" },
  registration_no: { description: "কোম্পানির নিবন্ধন সনদে থাকা registration number লিখুন।", example: "C-123456/2008" },
  company_bin: { description: "নথিতে থাকা প্রতিষ্ঠানের সঠিক BIN নম্বর লিখুন।", example: "নথি অনুযায়ী BIN" },
  company_tin: { description: "নথিতে থাকা প্রতিষ্ঠানের সঠিক TIN নম্বর লিখুন।", example: "নথি অনুযায়ী TIN" },
  company_website: { description: "https:// সহ সম্পূর্ণ website address লিখুন।", example: "https://example.com" },
  company_email: { description: "প্রতিষ্ঠানের সক্রিয় official email address লিখুন।", example: "info@example.com" },
  company_mobile: { description: "প্রধান যোগাযোগ নম্বর country code-সহ লিখুন।", example: "+8801XXXXXXXXX" },
  factory_address: { description: "কারখানার পূর্ণ ঠিকানা লিখুন; এলাকা, রাস্তা ও প্রয়োজনীয় landmark দিন।", example: "Plot 12, BSCIC Industrial Area" },
  company_address: { description: "প্রতিষ্ঠানের corporate বা registered office-এর পূর্ণ ঠিকানা লিখুন।", example: "House 10, Road 5, Dhaka" },
  district_territory: { description: "যে জেলা বা sales territory-এর অধীনে accountটি পরিচালিত হয় তা লিখুন।", example: "Dhaka North" },
  city_region: { description: "প্রতিষ্ঠানের শহর বা region লিখুন।", example: "Dhaka" },
  company_country_id: { description: "তালিকা থেকে প্রতিষ্ঠানের দেশ বেছে নিন।", example: "Bangladesh" },
  annual_turnover: { description: "সর্বশেষ জানা অর্থবছরের বার্ষিক turnover-এর অঙ্ক লিখুন; comma ছাড়া সংখ্যা দিন।", example: "250000000" },
  turnover_currency: { description: "Turnover-এর অঙ্ক যে currency-তে, সেটি বেছে নিন।", example: "BDT" },
  employee_count: { description: "প্রতিষ্ঠানের মোট কর্মীর আনুমানিক সংখ্যা লিখুন।", example: "150" },
  annual_chemical_consumption: { description: "এক বছরে ব্যবহৃত chemical-এর পরিমাণ সংখ্যা দিয়ে লিখুন।", example: "1200" },
  consumption_unit: { description: "Chemical consumption-এর একক বেছে নিন, যেমন kg বা ton।", example: "Ton" },
  estimated_total_wallet: { description: "গ্রাহকের মোট সম্ভাব্য বার্ষিক ক্রয় বা category spend-এর estimate লিখুন।", example: "50000000" },
  estimate_period: { description: "Estimateটি কোন সময়ের জন্য তা উল্লেখ করুন।", example: "FY 2025-26" },
  estimate_source: { description: "Estimate-এর উৎস লিখুন, যেমন customer meeting, report বা customer-provided data।", example: "Customer meeting" },
  estimate_confirmed_at: { description: "Estimateটি শেষ কবে যাচাই বা নিশ্চিত হয়েছে সেই তারিখ দিন।", example: "2026-09-30" },
  dealing_person_id: { description: "এই account-এর দায়িত্বে থাকা account owner তালিকা থেকে বেছে নিন।", example: "তালিকা থেকে দায়িত্বপ্রাপ্ত ব্যক্তি" },
  onboarding_date: { description: "গ্রাহক হিসেবে onboarding-এর তারিখ দিন।", example: "2026-01-15" },
  credit_terms: { description: "অনুমোদিত credit period দিন; দিনের সংখ্যা লিখুন।", example: "30" },
  credit_limit: { description: "অনুমোদিত credit limit-এর অঙ্ক লিখুন; সংশ্লিষ্ট currency ও অনুমোদন যাচাই করুন।", example: "1000000" },
  required_functions: { description: "Stakeholder coverage-এর জন্য প্রয়োজনীয় department/function tag হিসেবে দিন।", example: "Procurement, Finance" },
  selected_tier: { description: "অনুমোদিত account classification তালিকা থেকে বেছে নিন।", example: "Strategic" },
  selection_reason: { description: "এই tier বেছে নেওয়ার ব্যবসায়িক কারণ সংক্ষেপে লিখুন।", example: "উচ্চ বার্ষিক সম্ভাবনা ও দীর্ঘমেয়াদি growth plan" },
  classification_review_date: { description: "Account tier পর্যালোচনার পরবর্তী তারিখ দিন।", example: "2027-01-15" },
  payment_behavior_notes: { description: "যাচাই করা payment history ও আচরণ লিখুন; অনুমানভিত্তিক মন্তব্য দেবেন না।", example: "গত ৬টি invoice সময়মতো পরিশোধ করেছে" },
  competitor_pricing_notes: { description: "প্রতিযোগীর pricing বা বাজারের তথ্যের উৎসসহ সংক্ষিপ্ত note লিখুন।", example: "Customer meeting, Sep 2026: competitor quote was lower by 5%" },
};
const recordConfig = computed(() => forms[recordType.value]);
function cleanErrors() {
  formError.value = "";
  fieldErrors.value = {};
}
function restricted(key) {
  if (
    [
      "credit_limit",
      "credit_terms",
      "company_bin",
      "company_tin",
      "payment_behavior_notes",
    ].includes(key)
  )
    return !allowed("credit", "update");
  if (["selected_tier", "selection_reason", "classification_review_date"].includes(key))
    return !allowed("sam-performance", "update");
  return false;
}
function openBasic() {
  cleanErrors();
  basicForm.value = JSON.parse(JSON.stringify({ ...customer.value, ...profile.value }));
  formVersion = profile.value.profile_version;
  originalBasic = JSON.stringify(basicForm.value);
  basicOpen.value = true;
  loadLookups();
}
function closeBasic() {
  if (
    JSON.stringify(basicForm.value) !== originalBasic &&
    !window.confirm("Discard unsaved profile changes?")
  )
    return;
  basicOpen.value = false;
}
function edit(type, item = null, preset = {}) {
  cleanErrors();
  recordType.value = type;
  recordId.value = item?.id || null;
  formVersion = profile.value.profile_version;
  recordForm.value = JSON.parse(
    JSON.stringify(
      item || {
        period_year: new Date().getFullYear(),
        record_date: today(),
        activity_at: today(),
        survey_date: today(),
        activity_status: "Open",
        action_status: "Open",
        gap_status: "Identified",
        stage: "Sampled",
        currency: "BDT",
        our_volume: 0,
        competitor_volume: 0,
        champion: false,
        blocker: false,
        is_primary: false,
        ...preset,
      },
    ),
  );
  for (const f of forms[type].fields)
    if (f.type === "date" && recordForm.value[f.key])
      recordForm.value[f.key] = String(recordForm.value[f.key]).slice(0, 10);
  originalRecord = JSON.stringify(recordForm.value);
  recordOpen.value = true;
  loadLookups();
}
function closeRecord() {
  if (
    JSON.stringify(recordForm.value) !== originalRecord &&
    !window.confirm("Discard unsaved record changes?")
  )
    return;
  recordOpen.value = false;
}
function today() {
  const d = new Date();
  return new Date(d.valueOf() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
const RecordFields = defineComponent({
  setup() {
    return () =>
      h("div", {}, [
        formError.value ? h("div", { class: "form-error", role: "alert" }, formError.value) : null,
        ...recordConfig.value.fields.map((f) =>
          h("div", { class: "mb-4", key: f.key }, [
            h(WorkspaceField, {
              field: f,
              modelValue: recordForm.value[f.key],
              lookups: lookups.value,
              error: fieldErrors.value["payload." + f.key],
              "onUpdate:modelValue": (v) => (recordForm.value[f.key] = v),
              onSearch: searchProducts,
            }),
          ]),
        ),
        recordType.value === "product_gap"
          ? h(
              "div",
              { class: "calculated-preview" },
              "Preview · Gap volume: " +
                number(
                  Math.max(
                    0,
                    Number(recordForm.value.annual_requirement || 0) -
                      Number(recordForm.value.our_volume || 0),
                  ),
                ) +
                " " +
                (recordForm.value.uom || "") +
                " · Gap value: " +
                money(
                  Math.max(
                    0,
                    Number(recordForm.value.annual_requirement || 0) -
                      Number(recordForm.value.our_volume || 0),
                  ) * Number(recordForm.value.expected_price || 0),
                  recordForm.value.currency,
                ),
            )
          : null,
      ]);
  },
});
async function saveBasic() {
  const payload = { profile_version: formVersion };
  for (const s of basicSections)
    for (const f of s.fields)
      if (!restricted(f.key)) payload[f.key] = basicForm.value[f.key] ?? null;
  await save(
    () => Axios.put(customerProfileSamUrl(customerId.value), payload),
    () => (basicOpen.value = false),
  );
}
async function saveRecord() {
  const payload = {};
  for (const f of recordConfig.value.fields) payload[f.key] = recordForm.value[f.key] ?? null;
  await save(
    () =>
      Axios.post(customerProfileSamRecordUrl(customerId.value, recordType.value), {
        id: recordId.value,
        profile_version: formVersion,
        payload,
      }),
    () => (recordOpen.value = false),
  );
}
async function save(run, close) {
  if (saving.value) return;
  saving.value = true;
  cleanErrors();
  try {
    const r = await run();
    data.value = r.data.data;
    close();
    notice.value = "Saved successfully";
    noticeOpen.value = true;
  } catch (e) {
    if (e.response?.status === 409) {
      conflictOpen.value = true;
    } else {
      fieldErrors.value = e.response?.data?.errors || {};
      formError.value =
        Object.values(fieldErrors.value).flat().join(" ") ||
        e.response?.data?.message ||
        "Unable to save. Your changes are still in this form.";
    }
  } finally {
    saving.value = false;
  }
}
async function refreshConflict() {
  basicOpen.value = false;
  recordOpen.value = false;
  conflictOpen.value = false;
  await reload();
}
const reportOpen = ref(false),
  reportKind = ref("Account Summary");
function printSummary(kind = "Account Summary") {
  reportKind.value = typeof kind === "string" ? kind : "Account Summary";
  reportOpen.value = true;
}
watch(customerId, reload, { immediate: true });
watch(selectedEmployeeId, () => {
  accounts.value = [];
  searchAccounts();
});
watch(
  () => route.query.tab,
  (v) => {
    tab.value = { gap: "gap-analysis", sam: "sam-performance" }[v] || v || "overview";
  },
  { immediate: true },
);
watch(tab, (v) => {
  if (v !== route.query.tab) router.replace({ query: { ...route.query, tab: v } });
  if (v === "reports") loadPortfolio();
});
onBeforeRouteLeave(() => {
  if (
    (basicOpen.value || recordOpen.value) &&
    !window.confirm("Leave this page without saving the open form?")
  )
    return false;
});
function unload(e) {
  if (basicOpen.value || recordOpen.value) {
    e.preventDefault();
    e.returnValue = "";
  }
}
window.addEventListener("beforeunload", unload);
searchAccounts();
onBeforeUnmount(() => {
  loadSeq++;
  searchSeq++;
  portfolioSeq++;
  clearTimeout(accountTimer);
  clearTimeout(productTimer);
  window.removeEventListener("beforeunload", unload);
});
</script>

<style scoped>
.customer-workspace {
  --ink: #0f1729;
  --muted: #8a93a6;
  --border: #e4e8f0;
  --bg: #eef1f7;
  background: var(--bg);
  color: var(--ink);
  font-family: Inter, Arial, sans-serif;
  min-height: calc(100vh - 64px);
}
.workspace-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: #fff;
  padding: 14px 24px;
  border-bottom: 1px solid var(--border);
}
.customer-select {
  max-width: 460px;
  min-width: 240px;
}
.profile-header {
  background: white;
  border-bottom: 1px solid var(--border);
  padding: 20px 24px 0;
}
.profile-header h1 {
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.profile-meta {
  font-size: 12px;
  color: var(--muted);
  margin-top: 7px;
}
.profile-meta b {
  color: #4b5567;
}
.completion-button {
  display: flex;
  gap: 12px;
  align-items: center;
  text-align: left;
  color: var(--muted);
  font-size: 12px;
}
.completion-button b {
  color: #2e56e8;
}
.completion-button .v-progress-circular {
  font-size: 11px;
  font-weight: 700;
}
.workspace-content {
  padding: 24px;
  max-width: 1400px;
  margin: auto;
}
.workspace-empty {
  padding: 80px 24px;
  text-align: center;
}
.workspace-empty h1 {
  font-size: 24px;
  margin: 20px 0 8px;
}
.workspace-empty p {
  color: #8a93a6;
  margin-top: 12px;
}
.workspace-content :deep(.v-card) {
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #fff;
}
.workspace-content :deep(.workspace-panel) {
  padding: 16px;
}
.customer-workspace :deep(.v-tab) {
  font-size: 12px;
  letter-spacing: 0;
  text-transform: none;
}
.customer-workspace :deep(.v-btn) {
  text-transform: none;
  letter-spacing: 0;
}
.customer-workspace :deep(.stats-grid) {
  display: grid;
  grid-template-columns: repeat(var(--stat-columns), minmax(0, 1fr));
  gap: 12px;
}
.customer-workspace :deep(.stat-card) {
  padding: 16px;
}
.customer-workspace :deep(.stat-label) {
  font-size: 11.8px;
  font-weight: 700;
  color: #8a93a6;
}
.customer-workspace :deep(.stat-value) {
  font-size: 23px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin-top: 3px;
  overflow-wrap: anywhere;
}
.customer-workspace :deep(.stat-value small) {
  font-size: 12px;
  font-weight: 600;
  color: #8a93a6;
}
.customer-workspace :deep(.stat-foot) {
  font-size: 11.8px;
  color: #8a93a6;
  margin-top: 4px;
}
.customer-workspace :deep(.card-title-row) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.customer-workspace :deep(.card-title-row h2) {
  font-size: 14px;
  font-weight: 700;
}
.customer-workspace :deep(.card-eyebrow) {
  font-size: 12px;
  color: #8a93a6;
  margin-top: 3px;
}
.kv-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.kv-grid .k {
  font-size: 11.6px;
  color: #8a93a6;
  font-weight: 600;
}
.kv-grid .v {
  font-size: 13.3px;
  color: #0f1729;
  font-weight: 600;
  margin-top: 2px;
  overflow-wrap: anywhere;
}
.kv-grid .full {
  grid-column: 1/-1;
}
.bar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12.6px;
  margin-bottom: 6px;
}
.bar-row b {
  font-size: 12px;
}
.empty-note {
  font-size: 13px;
  color: #8a93a6;
  padding: 20px 0;
  line-height: 1.6;
}
.source-note {
  font-size: 11px;
  color: #8a93a6;
  margin-top: 14px;
  line-height: 1.5;
}
.matrix {
  display: block;
  width: 100%;
  height: auto;
}
.swot-item {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  margin: 18px 0;
}
.swot-item small {
  color: #8a93a6;
}
.timeline-item {
  display: flex;
  gap: 18px;
  font-size: 12px;
}
.timeline-item b {
  min-width: 86px;
}
.chart-empty {
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #8a93a6;
  font-size: 12px;
  gap: 10px;
}
.sam-subtabs {
  margin-bottom: 16px;
  background: white;
  border-radius: 10px;
}
.score-strip {
  height: 14px;
  display: flex;
  overflow: hidden;
  border-radius: 7px;
  margin: 14px 0;
}
.legend-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #4b5567;
}
.customer-workspace :deep(.v-table th) {
  font-size: 11px;
  color: #8a93a6;
  font-weight: 700;
  white-space: nowrap;
}
.customer-workspace :deep(.v-table td) {
  font-size: 12px;
  color: #4b5567;
}
.customer-workspace :deep(.v-data-table-footer) {
  font-size: 11px;
}
.customer-workspace :deep(.record-empty) {
  padding: 24px;
  color: #8a93a6;
}
.workspace-dialog .dialog-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e4e8f0;
  font-size: 16px;
  padding: 12px 20px;
}
.dialog-title-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.entry-guide-dialog .dialog-title {
  align-items: flex-start;
}
.entry-guide-subtitle {
  margin-top: 4px;
  color: #8a93a6;
  font-size: 12px;
  font-weight: 400;
}
.entry-guide-content {
  line-height: 1.65;
}
.entry-guide-section + .entry-guide-section {
  margin-top: 24px;
}
.entry-guide-section h3 {
  margin: 0 0 10px;
  color: #52629e;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.entry-guide-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.entry-guide-item {
  min-width: 0;
  padding: 12px;
  border: 1px solid #e4e8f0;
  border-radius: 8px;
  background: #fafbfe;
}
.entry-guide-field-label {
  color: #0f1729;
  font-size: 12px;
  font-weight: 700;
}
.entry-guide-item p {
  margin: 5px 0 0;
  color: #4b5567;
  font-size: 12px;
}
.entry-guide-example {
  margin-top: 7px;
  color: #52629e;
  font-size: 11px;
  overflow-wrap: anywhere;
}
.section-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8a93a6;
  margin: 16px 0;
}
.form-error {
  background: #fce7e8;
  color: #b42318;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.calculated-preview {
  padding: 14px;
  background: #eaf0fe;
  color: #2e56e8;
  border-radius: 10px;
  font-size: 12px;
}
.clickable {
  cursor: pointer;
}
:global(.activity-drawer) {
  position: absolute !important;
  right: 0;
  top: 0;
  bottom: 0;
  margin: 0 !important;
  max-height: 100% !important;
  height: 100% !important;
}
:global(.activity-drawer .v-card) {
  height: 100%;
  border-radius: 0 !important;
}
:global(.activity-drawer .v-card-text) {
  overflow-y: auto;
}
.workspace-content h3 {
  font-size: 14px;
}
@media (max-width: 1100px) {
  .customer-workspace :deep(.stats-grid) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .workspace-content {
    padding: 16px;
  }
}
@media (max-width: 600px) {
  .customer-workspace :deep(.stats-grid) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .workspace-toolbar,
  .profile-header {
    padding-left: 12px;
    padding-right: 12px;
  }
  .workspace-content {
    padding: 12px;
  }
  .customer-select {
    max-width: 100%;
    flex-basis: 100%;
  }
  .profile-header h1 {
    font-size: 18px;
  }
  .kv-grid {
    grid-template-columns: 1fr;
  }
  .entry-guide-grid {
    grid-template-columns: 1fr;
  }
  .dialog-title-actions :deep(.v-btn) {
    font-size: 11px;
  }
  .timeline-item {
    flex-direction: column;
    gap: 4px;
  }
}
@media print {
  .workspace-toolbar,
  .completion-button,
  .profile-header :deep(.v-tabs),
  .customer-workspace :deep(button) {
    display: none !important;
  }
  .workspace-content {
    max-width: none;
    padding: 0;
  }
  .customer-workspace {
    background: white;
  }
  .workspace-content :deep(.v-card) {
    break-inside: avoid;
  }
}
</style>
